import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from 'environments/environment';
import { catchError } from 'rxjs/operators';
import {
  ManagerCompany,
  ManagerCompanyUser,
  ClientRole,
  UserInvitation,
  CustomerManagerCompany,
  CustomerUser,
  ManagerCustomerUser,
  ManagerCustomerMeteringPoint,
  ManagerJobLockStatus
} from '@model';
import { SxErrorService } from '../../../services/sx-error.service';

@Injectable({
  providedIn: 'root'
})
export class ManagerService {
 
  constructor(private http: HttpClient, private sxErrorService: SxErrorService) {}

  getMyManagerCompanies(): Observable<ManagerCompany[]> {
    return this.http
      .get<ManagerCompany[]>(`${environment.apiEndpoint}/managercompanies`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getMyClientRoles(): Observable<ClientRole[]> {
    return this.http
      .get<ClientRole[]>(`${environment.apiEndpoint}/managercompanies/clientroles`)
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getMyManagerCompaniesUsers(managerCompanyId: number): Observable<ManagerCompanyUser[]> {
    return this.http
      .get<ManagerCompanyUser[]>(`${environment.apiEndpoint}/managercompanies/${managerCompanyId}/users`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  bulkCreateManagerCompanyInvitations(invitations: UserInvitation[]): Observable<UserInvitation[]> {
    return this.http
      .post<UserInvitation[]>(
        `${environment.apiEndpoint}/managercompanies/${invitations[0].managerCompanyId}/invitations`,
        invitations
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getMyManagerCompanyInvitations(managerCompanyId: number): Observable<UserInvitation[]> {
    return this.http
      .get<UserInvitation[]>(`${environment.apiEndpoint}/managercompanies/${managerCompanyId}/invitations`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  removeManagerCompanyInvitation(invitation: UserInvitation): Observable<UserInvitation> {
    return this.http
      .delete<any>(
        `${environment.apiEndpoint}/managercompanies/${invitation.managerCompanyId}/invitations/${invitation.id}`
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  reSendManagerCompanyInvitation(invitation: UserInvitation): Observable<UserInvitation[]> {
    return this.http
      .post<UserInvitation[]>(
        `${environment.apiEndpoint}/managercompanies/${invitation.managerCompanyId}/invitations/${invitation.id}/resend`,
        invitation
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  updateManagerCompanyUser(user: ManagerCompanyUser): Observable<ManagerCompanyUser> {
    return this.http
      .post<ManagerCompanyUser>(
        `${environment.apiEndpoint}/managercompanies/${user.managerCompanyId}/users/${user.userId}`,
        user
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  bulkUpdateManagerCompanyUser(user: ManagerCompanyUser): Observable<ManagerJobLockStatus> {
    return this.http
      .post<ManagerJobLockStatus>(
        `${environment.apiEndpoint}/managercompanies/${user.managerCompanyId}/users/${user.userId}/bulkupdate`,
        user
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getMyManagerCustomerUsers(managerCompanyId: number, customerId: string): Observable<ManagerCustomerUser[]> {
    return this.http
      .get<ManagerCustomerUser[]>(
        `${environment.apiEndpoint}/managercompanies/${managerCompanyId}/customers/${customerId}/users`
      )
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getMyManagerCustomersUsers(managerCompanyId: any, customerIds: string[]): Observable<ManagerCustomerUser[]> {
    return this.http
    .post<ManagerCustomerUser[]>(
      `${environment.apiEndpoint}/managercompanies/${managerCompanyId}/customers/users`,
      customerIds
    )
    .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getMyManagerCompanyUserCustomers(managerCompanyId: number, userId: string) {
    return this.http
      .get<ManagerCustomerUser[]>(
        `${environment.apiEndpoint}/managercompanies/${managerCompanyId}/users/${userId}/customers`
      )
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getManagerCustomerMeteringPoints(indata: CustomerManagerCompany): Observable<ManagerCustomerMeteringPoint[]> {
    return this.http
      .get<ManagerCustomerMeteringPoint[]>(
        `${environment.apiEndpoint}/managercompanies/${indata.managerCompanyId}/customers/${indata.customerId}/meteringpoints`
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  bulkAddManagerCompanyUsersToCustomer(users: ManagerCustomerUser[]): Observable<ManagerCustomerUser[]> {
    return this.http
      .post<ManagerCustomerUser[]>(
        `${environment.apiEndpoint}/managercompanies/${users[0].managerCompanyId}/customers/${users[0].customerId}/users`,
        users
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  lockManagerCustomerUsersForUpdate(users: ManagerCustomerUser[]): Observable<ManagerJobLockStatus> {
    return this.http
      .post<ManagerJobLockStatus>(
        `${environment.apiEndpoint}/managercompanies/${users[0].managerCompanyId}/customers/users/${users[0].userId}/lockforupdate`,
        users
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  updateManagerCustomerUser(indata: ManagerCustomerUser): Observable<ManagerCustomerUser> {
    return this.http
      .post<ManagerCustomerUser>(
        `${environment.apiEndpoint}/managercompanies/${indata.managerCompanyId}/customers/${indata.customerId}/users/${indata.userId}`,
        indata
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  removeManagerCustomerUser(user: ManagerCustomerUser): Observable<ManagerCustomerUser> {
    return this.http
      .post<ManagerCustomerUser>(
        `${environment.apiEndpoint}/managercompanies/${user.managerCompanyId}/customers/${user.customerId}/users/${user.userId}/delete`,
        user
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  removeManagerCustomer(customer: CustomerManagerCompany): Observable<CustomerManagerCompany> {
    return this.http
      .post<CustomerManagerCompany>(
        `${environment.apiEndpoint}/managercompanies/${customer.managerCompanyId}/customers/${customer.customerId}/delete`,
        customer
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  lockManagerCompanyUserForDelete(user: ManagerCompanyUser): Observable<ManagerJobLockStatus> {
    return this.http
      .post<ManagerJobLockStatus>(
        `${environment.apiEndpoint}/managercompanies/${user.managerCompanyId}/users/${user.userId}/lockfordelete`,
        user
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  removeManagerCompany(company: ManagerCompany): Observable<ManagerCompany> {
    return this.http
      .delete<ManagerCompany>(`${environment.apiEndpoint}/managercompanies/${company.id}`)
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getManagerCompanyCustomers(managerCompanyId: number): Observable<CustomerManagerCompany[]> {
    return this.http
      .get<CustomerManagerCompany[]>(`${environment.apiEndpoint}/ManagerCompanies/${managerCompanyId}/Customers`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getIsManagerCompanyUserExclusiveAdministrator(user: ManagerCompanyUser): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${environment.apiEndpoint}/managercompanies/${user.managerCompanyId}/users/${user.userId}/IsExclusiveAdministrator`,
        user
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getIsManagerCompanyUserLockedForUpdate(user: ManagerCompanyUser): Observable<ManagerJobLockStatus> {
    return this.http
      .post<ManagerJobLockStatus>(
        `${environment.apiEndpoint}/managercompanies/${user.managerCompanyId}/users/${user.userId}/IsLockedForUpdate`,
        user
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getManagerCompanyUsersLockedForUpdate(managerCompanyId: number): Observable<ManagerJobLockStatus[]> {
    return this.http
      .get<ManagerJobLockStatus[]>(
        `${environment.apiEndpoint}/managerCompanies/${managerCompanyId}/users/IsLockedForUpdate`
      )
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  setPrimaryContact(primaryContact: ManagerCompanyUser): Observable<ManagerCompanyUser> {
    return this.http
      .post<ManagerCompanyUser>(
        `${environment.apiEndpoint}/managercompanies/${primaryContact.managerCompanyId}/users/${primaryContact.userId}/SetPrimaryContact`,
        primaryContact
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }
}
